import React from "react";
import { Pie, Bar } from "react-chartjs-2";
import { fxStreetNews } from "./fxStreet";
import { useAppContext } from "../../App"; // Import AppContext
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import moment from "moment-timezone"; // Import moment-timezone for timezone conversion
import NewsAndSentimentTable from "./NewsAndSentimentTable";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels, CategoryScale, LinearScale, BarElement);

const NewsAndSentiment = ({ styles }) => {
    const { currencyPair, timeZone } = useAppContext(); // Destructure values from AppContext

    const { newsData, loading, error } = fxStreetNews(currencyPair);

    if (loading) return <p>Loading news and sentiment...</p>;
    if (error) return <p style={{ color: "red" }}>{error}</p>;

    // Sort articles by timestamp in descending order (latest first)
    const sortedNewsData = [...newsData].sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );

    // Process sentiment data for pie chart
    const sentimentCounts = {
        positive: 0,
        neutral: 0,
        negative: 0,
    };

    const sentimentByDate = {};

    sortedNewsData.forEach((article) => {
        // Count for pie chart
        sentimentCounts[article.sentiment] =
            (sentimentCounts[article.sentiment] || 0) + 1;

        // Organize for stacked bar chart
        const date = moment(article.timestamp)
            .tz(timeZone)
            .format("YYYY-MM-DD");
        if (!sentimentByDate[date]) {
            sentimentByDate[date] = { positive: 0, neutral: 0, negative: 0 };
        }
        sentimentByDate[date][article.sentiment]++;
    });

    const totalArticles = Object.values(sentimentCounts).reduce((a, b) => a + b, 0);

    // Prepare data for pie chart
    const pieChartData = {
        labels: ["Positive", "Neutral", "Negative"],
        datasets: [
            {
                data: [
                    sentimentCounts.positive,
                    sentimentCounts.neutral,
                    sentimentCounts.negative,
                ],
                backgroundColor: ["#4CAF50", "#FFC107", "#F44336"],
                hoverBackgroundColor: ["#66BB6A", "#FFD54F", "#EF5350"],
            },
        ],
    };

    const pieChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: "top",
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) =>
                        `${tooltipItem.label}: ${tooltipItem.raw} articles`,
                },
            },
            datalabels: {
                formatter: (value) => {
                    const percentage = ((value / totalArticles) * 100).toFixed(1);
                    return `${percentage}%`;
                },
                color: "#fff",
                font: {
                    weight: "bold",
                },
            },
        },
    };

    // Prepare data for stacked bar chart
    const dates = Object.keys(sentimentByDate).filter((date) => {
        const { positive, neutral, negative } = sentimentByDate[date];
        return positive || neutral || negative; // Include only dates with at least one non-zero value
    });

    const stackedBarData = {
        labels: dates,
        datasets: [
            {
                label: "Positive",
                data: dates.map((date) => sentimentByDate[date].positive || 0),
                backgroundColor: "#4CAF50",
                stack: "sentiments", // Ensures stacking
            },
            {
                label: "Neutral",
                data: dates.map((date) => sentimentByDate[date].neutral || 0),
                backgroundColor: "#FFC107",
                stack: "sentiments",
            },
            {
                label: "Negative",
                data: dates.map((date) => sentimentByDate[date].negative || 0),
                backgroundColor: "#F44336",
                stack: "sentiments",
            },
        ],
    };

    const stackedBarOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: "top",
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        const value = tooltipItem.raw;
                        return value > 0 ? `${tooltipItem.dataset.label}: ${value} articles` : null;
                    },
                },
            },
        },
        scales: {
            x: {
                stacked: true,
                title: {
                    display: true,
                    text: "Date",
                },
            },
            y: {
                stacked: true,
                title: {
                    display: true,
                    text: "Number of Articles",
                },
                beginAtZero: true,
                ticks: {
                    callback: (value) => (value > 0 ? value : ""),
                },
            },
        },
    };

    const headerClassName = styles?.header || "";
    const titleClassName = styles?.chartTitle || "";

    return (
        <div className={headerClassName}>
            <h2 className={titleClassName}>Sentiment Distribution</h2>
            <Pie data={pieChartData} options={pieChartOptions} />
            <h2 className={titleClassName}>Sentiment Distribution by Date</h2>
            <Bar data={stackedBarData} options={stackedBarOptions} />
           
        </div>
    );
};

export default NewsAndSentiment;
